import React, { useEffect } from 'react';
import { useDeviceSize } from 'common/lib/hooks/useDeviceSize';
import PropTypes from 'prop-types';
import CompanyProfile from '../components/companyProfile';
import AdDescription from '../components/adDescription';
import AdHeading from '../components/adHeading';
import ContentMap from '../components/map';
import EndNotes from '../components/endNotes';
import ImageCarousel from '../components/imageCarousel';
import ProgressBar from '../components/progressBar';
import UnitSelector from '../components/unitSelector';
import AdFinancing from '../components/adFinancing';
import AdFacilities from '../components/adFacilities';
import OpenHouse from '../components/openHouse';
import InterestListButton from '../components/interestListButton';
import HighlightedUnits from '../components/highlightedUnits';
import ShortDescription from '../components/shortDescription';
import NeighbourhoodProfile from '../components/neighbourhood';
import ImproveVisibilityPanel from '../components/improveVisibility';
import CollapsableText from '../components/collapsableText';
import ExternalContent from '../components/externalContent';
import UsefulLinks from '../components/usefulLinks';
import KeyInfo from '../components/keyInfo';
import SharingOptions from '../components/sharingOptions';
import SearchHeader from '../components/searchHeader';

const AppPtsMaster = ({ data }) => {
    const {
        userId,
        createdBy,
        latitude,
        longitude,
        streetAddress,
        localAreaName,
        postNumber,
        postArea,
        adStageStatusOne,
        adStageStatusTwo,
        adStageStatusThree,
        adStageStatusFour,
        heading,
        finnkode,
        descriptionList,
        adStatus,
        projectName,
        facilities,
        calendarUrl,
        viewings,
        orgId,
        orderUrl,
        projectUrl,
        shortDescription,
        contentModule,
        layoutPath,
        projectPhase,
        externalAdId,
        lastUpdatedAt,
        viewingRegistrationUrl,
        moreInfoLinks,
        adType,
        communicationEmail,
    } = data;

    useEffect(() => {
        if (window.hj) {
            window.hj('event', 'view_page_nybygg_pts');
        }
    }, []);

    const { isMobile, isDesktop } = useDeviceSize();

    const mobileView = (
        <>
            <SharingOptions finnkode={finnkode} heading={heading} />

            <AdHeading
                mainTitle={projectName}
                secondaryTitle={heading}
                adStatus={adStatus}
                postNumber={postNumber}
                postArea={postArea}
                streetAddress={streetAddress}
                localAreaName={localAreaName}
            />

            <ProgressBar
                adStageStatusOne={adStageStatusOne}
                adStageStatusTwo={adStageStatusTwo}
                adStageStatusThree={adStageStatusThree}
                adStageStatusFour={adStageStatusFour}
                projectPhase={projectPhase}
            />

            <AdFinancing data={data} />

            <KeyInfo data={data} />

            <InterestListButton adStatus={adStatus} communicationEmail={communicationEmail} />

            <OpenHouse
                dates={viewings}
                calendarUrl={calendarUrl}
                finnkode={finnkode}
                address={streetAddress}
                viewingRegistrationUrl={viewingRegistrationUrl}
            />

            <ShortDescription text={shortDescription} title="Kort om prosjektet" withBorder />
            <AdFacilities facilities={facilities} />
            <HighlightedUnits />
            <UnitSelector />
            <CollapsableText>
                <AdDescription description={descriptionList} />
            </CollapsableText>

            <UsefulLinks projectUrl={projectUrl} orderUrl={orderUrl} moreInfoLinks={moreInfoLinks} orgId={orgId} />
        </>
    );

    const desktopView = (
        <div className="grid grid-cols-3 gap-32">
            <div className="col-span-2">
                <SharingOptions finnkode={finnkode} heading={heading} />

                <AdHeading
                    mainTitle={projectName}
                    secondaryTitle={heading}
                    adStatus={adStatus}
                    postNumber={postNumber}
                    postArea={postArea}
                    streetAddress={streetAddress}
                    localAreaName={localAreaName}
                />

                <AdFinancing data={data} />

                <KeyInfo data={data} />

                <ShortDescription text={shortDescription} title="Kort om prosjektet" withBorder />

                <AdFacilities facilities={facilities} />

                <HighlightedUnits />

                <UnitSelector />

                <CollapsableText>
                    <AdDescription description={descriptionList} />
                </CollapsableText>
            </div>

            <div>
                <div className="sticky top-64 mt-16" style={{ zIndex: 2 }}>
                    {/* z-index is added here to override the default z-index of position sticky. This is 2 here because the FINN header has index 3, and the side column should not overlay the header */}
                    <InterestListButton adStatus={adStatus} communicationEmail={communicationEmail} />

                    <OpenHouse
                        dates={viewings}
                        calendarUrl={calendarUrl}
                        finnkode={finnkode}
                        address={streetAddress}
                        viewingRegistrationUrl={viewingRegistrationUrl}
                    />

                    <UsefulLinks
                        projectUrl={projectUrl}
                        orderUrl={orderUrl}
                        moreInfoLinks={moreInfoLinks}
                        orgId={orgId}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div className="page-container">
            <SearchHeader finnkode={finnkode} adType={adType} />

            {isDesktop && (
                <ProgressBar
                    adStageStatusOne={adStageStatusOne}
                    adStageStatusTwo={adStageStatusTwo}
                    adStageStatusThree={adStageStatusThree}
                    adStageStatusFour={adStageStatusFour}
                    projectPhase={projectPhase}
                />
            )}

            <ImageCarousel />

            {isDesktop && userId === createdBy.toString() && (
                <ImproveVisibilityPanel finnkode={finnkode} orgId={orgId} adStatus={adStatus} layoutPath={layoutPath} />
            )}

            <CompanyProfile expandable data={data} />

            {isDesktop ? desktopView : mobileView}

            <ContentMap
                mobile={isMobile}
                longitude={longitude}
                latitude={latitude}
                postNumber={postNumber}
                postArea={postArea}
                streetAddress={streetAddress}
            />

            <NeighbourhoodProfile finnkode={finnkode} />

            <ExternalContent finnkode={finnkode} contentModule={contentModule} />

            <CompanyProfile data={data} />

            <EndNotes lastUpdatedAt={lastUpdatedAt} finnkode={finnkode} externalAdId={externalAdId} />
        </div>
    );
};

AppPtsMaster.propTypes = {
    data: PropTypes.object.isRequired,
};

export default AppPtsMaster;
